/**
 * ContextDescription — Inline summary of the active context.
 *
 * Sits right after the ContextGridSwitcher in the TopBar. Shows the persona's
 * description plus up to 4 chip-shaped feature shortcuts (the first 4 items
 * from the persona's mega-menu groups). Clicking a chip routes to that page.
 */

import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context-store'
import { GRC_CONTEXTS, getContextMeta } from '@/config/contexts'
import { getMegaMenuGroupsForContext } from '@/config/mega-menu'

const ACCENT: Record<string, string> = {
  amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  red: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
  violet: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
  orange: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30',
  teal: 'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/30',
  purple: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
  blue: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
  slate: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
  green: 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/30',
  cyan: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
  indigo: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
}

interface ChipProps {
  label: string
  icon: LucideIcon
  href: string
  accent: string
}

function Chip({ label, icon: Icon, href, accent }: ChipProps) {
  return (
    <Link
      to={href}
      title={label}
      className={cn(
        'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap',
        'transition-all hover:scale-[1.03] hover:shadow-sm',
        accent,
      )}
    >
      <Icon className="h-3 w-3" />
      <span className="hidden md:inline truncate max-w-[100px]">{label}</span>
    </Link>
  )
}

export function ContextDescription() {
  const { t } = useTranslation()
  const { activeContext } = useContextStore()
  const ctx = GRC_CONTEXTS[activeContext] ?? GRC_CONTEXTS['associe']
  const ctxMeta = getContextMeta(activeContext)
  const groups = getMegaMenuGroupsForContext(activeContext)
  const chips = groups
    .flatMap((g) => g.tiles.slice(0, 1).map((t) => ({ ...t, accent: g.accent ?? 'slate' })))
    .slice(0, 4)

  return (
    <div className="hidden md:flex items-center gap-2 min-w-0 shrink overflow-hidden">
      <span className="text-[11px] text-muted-foreground italic truncate max-w-[200px]">
        {ctxMeta.description}
      </span>
      <span className="text-muted-foreground/40 shrink-0">·</span>
      <div className="flex items-center gap-1 shrink overflow-hidden">
        {chips.map((c) => (
          <Chip
            key={c.href}
            label={c.label}
            icon={c.icon}
            href={c.href}
            accent={ACCENT[c.accent] ?? ACCENT.slate}
          />
        ))}
        {chips.length > 0 && (
          <Link
            to={ctx.defaultRoute}
            className="text-[10.5px] font-semibold text-primary hover:underline whitespace-nowrap flex items-center gap-0.5"
          >
            {t('shell.seeAll')} <ArrowRight className="h-2.5 w-2.5" />
          </Link>
        )}
      </div>
    </div>
  )
}
